import type { HqApi } from '@hq/api-sdk';

/**
 * 登录后缓存的机构个性化信息（登录页展示用）。
 *
 * 存储口径：`localStorage` 单键整块 JSON，**不设过期时间、退出登录不清除**，
 * 浏览器关闭再打开仍可见；每次登录成功后整体重写（后写覆盖先写）。
 */
export interface OrgBrand {
  orgId: string;
  /** 机构简称（`org_institution.org_short_name`）。 */
  orgShortName: string | null;
  /** 机构全称（`org_institution.org_name`）。 */
  orgName: string | null;
  /** 机构 logo（`sys_org_config.logo_url`，相对或绝对地址）。 */
  logoUrl: string | null;
  /** 本次写入时间（毫秒），仅用于排查，不参与过期判断。 */
  updatedAt: number;
}

/** 与登录态（`hq-ops-auth`，sessionStorage）分离：登录态失效不影响该键。 */
const KEY = 'hq-ops-org-brand';

const normalize = (value: string | null | undefined): string | null => {
  const text = value?.trim();
  return text ? text : null;
};

/** 读取上次登录保存的机构信息；损坏或全空时返回 null（登录页回退默认品牌）。 */
export function readOrgBrand(): OrgBrand | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<OrgBrand> | null;
    if (!parsed) return null;
    const brand: OrgBrand = {
      orgId: String(parsed.orgId ?? ''),
      orgShortName: normalize(parsed.orgShortName ?? null),
      orgName: normalize(parsed.orgName ?? null),
      logoUrl: normalize(parsed.logoUrl ?? null),
      updatedAt: typeof parsed.updatedAt === 'number' ? parsed.updatedAt : 0
    };
    if (!brand.orgShortName && !brand.orgName && !brand.logoUrl) return null;
    return brand;
  } catch {
    return null;
  }
}

/**
 * 登录成功后读取并重写机构信息。
 *
 * 登录接口只回 `orgName`，简称在 `GET /auth/orgs`、logo 在 `GET /auth/org-config`，
 * 两个接口都是 RequireAuthorization 的登录态接口；任一失败只降级为空值，不阻断登录。
 * 三项全空（如运营主体 org_id=0 且未配 logo）时不写盘，避免把已有信息覆盖成空。
 */
export async function saveOrgBrandFromLogin(
  api: Pick<HqApi, 'auth'>,
  fallback: { orgId: string; orgName?: string | null }
): Promise<OrgBrand | null> {
  const [orgs, orgCfg] = await Promise.all([
    api.auth.switchableOrgs().catch(() => null),
    api.auth.orgConfig().catch(() => null)
  ]);

  const current = orgs ? orgs.items.find((item) => String(item.orgId) === String(orgs.currentOrgId)) ?? null : null;

  const brand: OrgBrand = {
    orgId: String(fallback.orgId ?? ''),
    orgShortName: normalize(current?.orgShortName),
    orgName: normalize(orgCfg?.orgName ?? current?.orgName ?? fallback.orgName ?? null),
    logoUrl: normalize(orgCfg?.logoUrl),
    updatedAt: Date.now()
  };

  if (!brand.orgShortName && !brand.orgName && !brand.logoUrl) return null;

  try {
    localStorage.setItem(KEY, JSON.stringify(brand));
  } catch {
    // 隐私模式/存储配额异常：忽略写失败，登录流程照常
    return null;
  }
  return brand;
}
